/* ============================================================
   MEMORIES OF MOTION — PHOTOGRAPHS FROM THE BOOK

   The carousel on the book page (snippets/mom-book-photographs.liquid).

   Native scroll-snap does the sliding, so swipe, trackpad and keyboard
   scrolling all work with no script at all. This only adds what scrolling
   cannot: previous/next buttons, a "2 of 5" counter, arrow keys on the
   focused track, and an enlarged view in a <dialog>.

   Deliberately not Dawn's <slider-component>: that one is written around
   Dawn's own slider markup and class names, and this section uses the MoM
   design system's.
   ============================================================ */

class MomBookPhotographs extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-mbp-track]');
    this.slides = this.track ? Array.from(this.track.children) : [];
    this.controls = this.querySelector('[data-mbp-controls]');
    this.count = this.querySelector('[data-mbp-count]');
    this.buttons = Array.from(this.querySelectorAll('[data-mbp-dir]'));
    this.dialog = this.querySelector('[data-mbp-dialog]');

    this.querySelectorAll('[data-mbp-open]').forEach((btn) => {
      btn.addEventListener('click', () => this.open(btn));
    });

    if (this.dialog) {
      // A click on the backdrop lands on the dialog itself, not its content.
      this.dialog.addEventListener('click', (e) => {
        if (e.target === this.dialog) this.dialog.close();
      });
    }

    // One photograph needs no controls.
    if (!this.track || this.slides.length < 2) return;

    this.controls.hidden = false;
    this.buttons.forEach((btn) => {
      btn.addEventListener('click', () => this.go(Number(btn.dataset.mbpDir)));
    });
    this.track.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();
      this.go(e.key === 'ArrowRight' ? 1 : -1);
    });
    this.track.addEventListener('scroll', () => this.schedule(), { passive: true });
    window.addEventListener('resize', () => this.schedule());
    this.update();
  }

  step() {
    return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
  }

  go(direction) {
    this.track.scrollBy({ left: direction * this.step() });
  }

  schedule() {
    if (this.pending) return;
    this.pending = requestAnimationFrame(() => {
      this.pending = null;
      this.update();
    });
  }

  update() {
    const index = Math.min(Math.round(this.track.scrollLeft / this.step()), this.slides.length - 1);
    const atStart = this.track.scrollLeft <= 2;
    const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    // At the end the last slide may not reach the left edge; count it anyway.
    const shown = atEnd ? this.slides.length : index + 1;
    this.count.textContent = `${shown} of ${this.slides.length}`;
    this.buttons[0].disabled = atStart;
    this.buttons[1].disabled = atEnd;
  }

  open(btn) {
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
    const img = this.dialog.querySelector('[data-mbp-dialog-img]');
    img.src = btn.dataset.full;
    img.alt = btn.dataset.alt || '';
    this.dialog.querySelector('[data-mbp-dialog-cap]').textContent = btn.dataset.caption || '';
    this.dialog.showModal();
  }
}

if (!customElements.get('mom-book-photographs')) {
  customElements.define('mom-book-photographs', MomBookPhotographs);
}
